import React from 'react';
import {
  Show,
  SimpleShowLayout,
  TextField,
  DateField,
  EditButton,
  TopToolbar,
  BooleanField,
  ReferenceField,
  useRecordContext,
  ListButton,
} from 'react-admin';
import {
  Card,
  Typography,
  Box,
  Chip,
  Stack,
  Grid,
  Divider,
} from '@mui/material';
import {
  NotificationImportant as AlertIcon,
  Circle as StatusIcon,
} from '@mui/icons-material';
import DeleteWithPasswordButton from '@/components/DeleteWithPasswordButton';

const AlertShowActions = () => (
    <TopToolbar>
      <EditButton />
      <DeleteWithPasswordButton resource='alerts' label='Eliminar' />
      <ListButton />
    </TopToolbar>
);

const StatusDisplay = () => {
  const record = useRecordContext();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'error';
      case 'STANDBY':
        return 'warning';
      case 'ACKNOWLEDGED':
        return 'info';
      case 'INACTIVE':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'Activa';
      case 'STANDBY':
        return 'En espera';
      case 'ACKNOWLEDGED':
        return 'Reconocida';
      case 'INACTIVE':
        return 'Inactiva';
      default:
        return status;
    }
  };

  return (
    <Box display='flex' alignItems='center' gap={1}>
      <StatusIcon
        sx={{
          fontSize: 16,
          color:
            getStatusColor(record?.status) === 'error'
              ? 'error.main'
              : getStatusColor(record?.status) === 'warning'
                ? 'warning.main'
                : getStatusColor(record?.status) === 'info'
                  ? 'info.main'
                  : 'grey.500',
        }}
      />
      <Chip
        label={getStatusLabel(record?.status)}
        size='medium'
        color={getStatusColor(record?.status) as any}
        variant='outlined'
      />
    </Box>
  );
};

const RolePermissionsDisplay = () => {
  const record = useRecordContext();

  if (!record?.rolePermissions || record.rolePermissions.length === 0) {
    return (
      <Typography variant='body1' color='text.secondary'>
        Sin roles asignados
      </Typography>
    );
  }

  return (
    <Stack direction='column' spacing={1} flexWrap='wrap' useFlexGap>
      {record.rolePermissions.map((rp: any, index: number) => (
        <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Chip
            label={rp.role}
            size='medium'
            variant='outlined'
            color='primary'
          />
          <Chip
            label={rp.permissionLevel}
            size='small'
            variant='filled'
            color='secondary'
          />
        </Box>
      ))}
    </Stack>
  );
};

const LimitsDisplay = () => {
  const record = useRecordContext();
  console.log('LimitsDisplay - record:', record); // Debug

  const limits = [
    { label: 'Muy Alto', value: record?.veryHighLimit, color: 'error' },
    { label: 'Alto', value: record?.highLimit, color: 'warning' },
    { label: 'Bajo', value: record?.lowLimit, color: 'info' },
    { label: 'Muy Bajo', value: record?.veryLowLimit, color: 'primary' },
  ];

  const activeLimits = limits.filter(
    limit =>
      limit.value !== null && limit.value !== undefined && limit.value !== ''
  );

  console.log('LimitsDisplay - activeLimits:', activeLimits); // Debug

  if (activeLimits.length === 0) {
    return (
      <Typography variant='body1' color='text.secondary'>
        Sin límites configurados
      </Typography>
    );
  }

  return (
    <Grid container spacing={2}>
      {activeLimits.map((limit, index) => (
        <Grid item xs={6} sm={3} key={index}>
          <Card variant='outlined' sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant='caption' color='text.secondary'>
              {limit.label}
            </Typography>
            <Typography variant='h6' color={`${limit.color}.main`}>
              {limit.value}
            </Typography>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export const AlertsShow = () => {
  return (
    <Show actions={<AlertShowActions />}>
      <Box sx={{ padding: 3 }}>
        <Box display='flex' alignItems='center' mb={3}>
          <AlertIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant='h4' component='h1' fontWeight='bold'>
              Detalle de Alerta
            </Typography>
            <Typography variant='body1' color='text.secondary'>
              Información completa de la alerta seleccionada
            </Typography>
          </Box>
        </Box>

        <Card sx={{ boxShadow: 3 }}>
          <SimpleShowLayout>
            <Grid container spacing={3} sx={{ p: 3 }}>
              {/* Información básica */}
              <Grid item xs={12}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Información Básica
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField source='id' label='ID' />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField source='name' label='Nombre' />
              </Grid>
              <Grid item xs={12}>
                <TextField source='description' label='Descripción' />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField source='category' label='Categoría' />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField source='text' label='Texto de la Alerta' />
              </Grid>
              {/* Estado y configuración */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Estado y Configuración
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>{' '}
              <Grid item xs={12} md={6}>
                <Typography variant='subtitle2' gutterBottom>
                  Estado Actual
                </Typography>
                <StatusDisplay />
              </Grid>
              <Grid item xs={12} md={6}>
                <BooleanField source='isActive' label='Alerta Activa' />
              </Grid>
              {/* Límites */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Límites Configurados
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>{' '}
              <Grid item xs={12}>
                <LimitsDisplay />
              </Grid>
              {/* Referencias */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Referencias
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>{' '}
              <Grid item xs={12} md={6}>
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  gutterBottom
                >
                  Variable Asociada
                </Typography>{' '}
                <ReferenceField
                  source='variableId'
                  reference='variables'
                  link='show'
                  emptyText='Sin variable asociada'
                >
                  <TextField source='variableName' />
                </ReferenceField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography
                  variant='subtitle2'
                  color='text.secondary'
                  gutterBottom
                >
                  Zona Asociada
                </Typography>{' '}
                <ReferenceField
                  source='zoneId'
                  reference='zones'
                  link='show'
                  emptyText='Sin zona asociada'
                >
                  <TextField source='zoneName' />
                </ReferenceField>
              </Grid>
              {/* Roles de acceso */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Roles de Acceso
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>{' '}
              <Grid item xs={12}>
                <RolePermissionsDisplay />
              </Grid>
              {/* Fechas */}
              <Grid item xs={12} sx={{ mt: 3 }}>
                <Typography variant='h6' color='primary' gutterBottom>
                  Información de Fechas
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateField
                  source='created'
                  label='Fecha de Creación'
                  showTime
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateField
                  source='lastUpdate'
                  label='Última Actualización'
                  showTime
                />
              </Grid>
            </Grid>
          </SimpleShowLayout>
        </Card>
      </Box>
    </Show>
  );
};
